import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment-timezone';
import { FiCalendar, FiClock } from 'react-icons/fi';
import { Button } from 'rebass';
import classNames from 'classnames';
import AppDatePicker from 'components/AppDatePicker';
import { getDateFormatConfig, getStorageFormat, isDateTimeQuestion, isDateOnly } from 'utils/dateFormats';

/**
 * Converts a Date object into segmented fields for the input. When `timezone`
 * is supplied (researcher view) the segments are read in that timezone, so the
 * researcher sees the same calendar/clock values as the read-only display
 * (`formatReadOnlyValue`). Without `timezone` we fall back to browser-local —
 * the behavior the participant-facing form has always had.
 *
 * @param {Date|null} date - Date to convert
 * @param {boolean} showDate - Whether to populate date fields
 * @param {boolean} showTime - Whether to populate time fields
 * @param {string} [timezone] - IANA timezone to read segments in
 * @returns {Object} Segmented date object with month, day, year, hour, minute, ampm
 */
const getDateObject = (date, showDate, showTime, timezone) => {
    const dateObject = {
        month: '',
        day: '',
        year: '',
        hour: '',
        minute: '',
        ampm: 'AM'
    };

    if (date) {
        const m = timezone ? moment(date).tz(timezone) : moment(date);
        if (m.isValid()) {
            if (showDate) {
                dateObject.month = String(m.month() + 1).padStart(2, '0');
                dateObject.day = String(m.date()).padStart(2, '0');
                dateObject.year = String(m.year());
            }

            if (showTime) {
                const hours24 = m.hours();
                const hours12 = hours24 % 12 || 12;
                dateObject.hour = String(hours12).padStart(2, '0');
                dateObject.minute = String(m.minutes()).padStart(2, '0');
                dateObject.ampm = hours24 >= 12 ? 'PM' : 'AM';
            }
        }
    }

    return dateObject;
};

/**
 * Static props of each date segment. Their order and separator come from the
 * account's display format (`fieldOrder` / `separator`), never from this table.
 */
const DATE_SEGMENTS = {
    month: { placeholder: 'MM', maxLength: 2, max: 12, ariaLabel: 'Month' },
    day: { placeholder: 'DD', maxLength: 2, max: 31, ariaLabel: 'Day' },
    year: { placeholder: 'YYYY', maxLength: 4, max: 9999, ariaLabel: 'Year' }
};

const TIME_FIELDS = ['hour', 'minute', 'ampm'];

/**
 * @typedef {Object} DateSegmentProps
 * @property {string} value - Current value
 * @property {string} placeholder - Placeholder text
 * @property {number} maxLength - Max character length
 * @property {number} max - Maximum allowed value
 * @property {number} [min=1] - Minimum allowed value
 * @property {(value: string, shouldAutoAdvance?: boolean) => void} onValueChange - Callback when value changes
 * @property {(e: FocusEvent) => void} [onFocus] - Focus event handler
 * @property {(e: FocusEvent) => void} [onBlur] - Blur event handler
 * @property {(e: KeyboardEvent) => void} [onKeyDown] - Keydown event handler
 * @property {Object} inputRef - Ref for the input element
 * @property {string} ariaLabel - Accessibility label
 */

/**
 * Single numeric input segment for date/time (month, day, year, hour, minute)
 * @param {DateSegmentProps} props - Component props
 * @returns {JSX.Element} Input element
 */
const DateSegment = ({
    value,
    placeholder,
    maxLength,
    max,
    min = 1,
    onValueChange,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    ariaLabel
}) => {
    const handleChange = e => {
        const newValue = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
        const oldValue = String(value || '');

        // Always allow deletion or clearing (when newValue is shorter than current value or empty)
        if (newValue.length < oldValue.length || newValue === '') {
            onValueChange(newValue);
            return;
        }

        // Only validate when adding characters
        if (newValue.length <= maxLength) {
            const num = parseInt(newValue, 10);
            // Allow partial input, but if complete (maxLength), check against min and max
            if (newValue.length < maxLength || (num >= min && num <= max)) {
                onValueChange(newValue);
            }
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            const num = parseInt(value, 10);
            const newVal = isNaN(num) || value === '' ? min : num >= max ? min : num + 1; // We want to rotate the value when reaching max
            onValueChange(String(newVal).padStart(maxLength, '0'), false);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            const num = parseInt(value, 10);
            const newVal = isNaN(num) || value === '' ? max : num <= min ? max : num - 1; // Here exactly the same as above
            onValueChange(String(newVal).padStart(maxLength, '0'), false);
        } else if (e.key === 'Backspace') {
            // Let backspace work normally to delete characters
            // Only call custom handler if field is empty (to navigate back)
            if (!value && onKeyDown) {
                onKeyDown(e);
            }
            // Otherwise, let the default behavior delete the character
        } else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    return (
        <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            autoComplete="off"
            className="border-0 outline-0 text-center p-0 bg-transparent font-inherit"
            style={{
                width: `${maxLength * 1.6}ch`
            }}
        />
    );
};

/**
 * @typedef {Object} AmPmSegmentProps
 * @property {string} value - Current value ('AM' or 'PM')
 * @property {(value: string) => void} onValueChange - Callback when value changes
 * @property {(e: FocusEvent) => void} [onFocus] - Focus event handler
 * @property {(e: FocusEvent) => void} [onBlur] - Blur event handler
 * @property {(e: KeyboardEvent) => void} [onKeyDown] - Keydown event handler
 * @property {Object} inputRef - Ref for the input element
 * @property {string} ariaLabel - Accessibility label
 */

/**
 * AM/PM toggle input segment
 * @param {AmPmSegmentProps} props - Component props
 * @returns {JSX.Element} Input element
 */
const AmPmSegment = ({ value, onValueChange, onFocus, onBlur, onKeyDown, inputRef, ariaLabel }) => {
    const handleKeyDown = e => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            onValueChange(value === 'AM' ? 'PM' : 'AM');
        } else if (e.key === 'a' || e.key === 'A') {
            e.preventDefault();
            onValueChange('AM');
        } else if (e.key === 'p' || e.key === 'P') {
            e.preventDefault();
            onValueChange('PM');
        } else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleChange = e => {
        // Prevent default onChange behavior - value is controlled via onClick and keyboard events
        // We added it to prevent from an error message in the console
        e.preventDefault();
    };

    return (
        <input
            ref={inputRef}
            type="text"
            value={value || 'AM'}
            onChange={handleChange}
            onClick={() => onValueChange(value === 'AM' ? 'PM' : 'AM')}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            className="border-0 outline-0 text-center p-0 bg-transparent font-inherit"
            style={{
                width: `3.2ch`
            }}
        />
    );
};

/**
 * @typedef {Object} DateInputProps
 * @property {Date|null} value - Current date value
 * @property {(date: Date|null) => void} onChange - Callback when date changes
 * @property {string} [ariaLabelledby] - ID of element that labels this input
 * @property {boolean} [showTimeSelect] - Whether to show time selection with date
 * @property {boolean} [showTimeSelectOnly] - Whether to show only time selection
 * @property {string} [name] - Name attribute for hidden form input
 * @property {Object} [inputRef] - Ref for hidden form input
 */

/**
 * Segmented date/time input with calendar picker and hidden form field
 * @param {DateInputProps} props - Component props
 * @returns {JSX.Element} Segmented date input component with calendar
 */
export const DateInput = ({
    value,
    onChange,
    ariaLabelledby,
    showTimeSelect,
    showTimeSelectOnly,
    name,
    inputRef,
    timezone
}) => {
    const showDate = !showTimeSelectOnly;
    const showTime = showTimeSelect || showTimeSelectOnly;
    const monthRef = useRef(null);
    const dayRef = useRef(null);
    const yearRef = useRef(null);
    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const ampmRef = useRef(null);
    const [dateTime, setDateTime] = useState(() => getDateObject(value, showDate, showTime, timezone));
    const [focusedSegment, setFocusedSegment] = useState(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const { fieldOrder, separator } = getDateFormatConfig();
    const dateFields = showDate ? fieldOrder : [];
    // Tab/arrow order of every visible segment: the date in the format's order, then the time
    const segmentSequence = [...dateFields, ...(showTime ? TIME_FIELDS : [])];
    const segmentRefs = {
        month: monthRef,
        day: dayRef,
        year: yearRef,
        hour: hourRef,
        minute: minuteRef,
        ampm: ampmRef
    };
    const segmentAfter = field => segmentSequence[segmentSequence.indexOf(field) + 1];
    const segmentBefore = field => segmentSequence[segmentSequence.indexOf(field) - 1];
    const focusSegment = field => field && segmentRefs[field].current?.focus();
    const focusNext = field => focusSegment(segmentAfter(field));
    const focusPrevious = field => focusSegment(segmentBefore(field));

    /**
     * Converts 12-hour time to 24-hour format
     * @param {string} hour - Hour in 12-hour format
     * @param {string} minute - Minute value
     * @param {string} ap - AM/PM indicator
     * @returns {Object|null} Object with hours and minutes in 24-hour format, or null if invalid
     */
    const parseTime = (hour, minute, ap) => {
        if (!hour || !minute) return null;

        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);

        if (hourNum < 1 || hourNum > 12 || minuteNum < 0 || minuteNum > 59) {
            return null;
        }

        let hours24 = hourNum % 12;
        if (ap === 'PM') hours24 += 12;

        return { hours: hours24, minutes: minuteNum };
    };

    /**
     * Builds a Date object from segmented date/time values. When `timezone` is
     * supplied, the segments are interpreted in that timezone — so picking
     * "03:22 AM" in a Pacific researcher's view yields the same UTC instant
     * that 12:22 PM Warsaw originally produced. Without a timezone we keep the
     * browser-local construction the participant-facing form relies on.
     *
     * @param {Object} dateTime - Segmented date object with month, day, year, hour, minute, ampm
     * @returns {Date|null} Date object if valid, null otherwise
     */
    const buildDate = dateTime => {
        const { month, day, year, hour, minute, ampm } = dateTime;

        if (showTimeSelectOnly) {
            const time = parseTime(hour, minute, ampm);
            if (!time) return null;

            return moment()
                .hours(time.hours)
                .minutes(time.minutes)
                .seconds(0)
                .toDate();
        }

        // Date or Date+Time mode
        if (!month || !day || !year) return null;

        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);

        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || yearNum <= 0) {
            return null;
        }

        const dateObj = { year: yearNum, month: monthNum - 1, date: dayNum };

        if (showTime) {
            const time = parseTime(hour, minute, ampm);

            // If time is required but not provided, don't build the date
            if (!time) {
                return null;
            }

            dateObj.hours = time.hours;
            dateObj.minutes = time.minutes;
        }

        const isDateTime = isDateTimeQuestion(showTimeSelectOnly, showTimeSelect);
        const date = isDateTime && timezone ? moment.tz(dateObj, timezone) : moment(dateObj);
        return date.isValid() ? date.toDate() : null;
    };

    /**
     * Notifies parent component of date changes
     * @param {Object} dateTime - Segmented date object
     */
    const notifyChange = dateTime => {
        const date = buildDate(dateTime);
        onChange(date);
    };

    /**
     * Handles a date segment (month, day or year) change, auto-advancing to the
     * next segment once the current one is complete
     * @param {string} field - 'month' | 'day' | 'year'
     * @returns {(newValue: string, shouldAutoAdvance?: boolean) => void} Change handler
     */
    const handleDateFieldChange = field => (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, [field]: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === DATE_SEGMENTS[field].maxLength) {
            focusNext(field);
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles hour value changes with optional auto-advance to next field
     * @param {string} newValue - New hour value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleHourChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, hour: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 2) {
            minuteRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles minute value changes with optional auto-advance to next field
     * @param {string} newValue - New minute value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleMinuteChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, minute: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 2) {
            ampmRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles AM/PM value changes
     * @param {string} newValue - New AM/PM value
     */
    const handleAmpmChange = newValue => {
        const newDateTime = { ...dateTime, ampm: newValue };
        setDateTime(newDateTime);
        notifyChange(newDateTime);
    };

    /**
     * Handles keyboard navigation for a date segment: ArrowRight (or the format's
     * separator once the segment has a value) moves forward, ArrowLeft or
     * Backspace on an empty segment moves back
     * @param {string} field - 'month' | 'day' | 'year'
     * @returns {(e: KeyboardEvent) => void} Keydown handler
     */
    const handleDateFieldKeyDown = field => e => {
        const nextDateField = dateFields[dateFields.indexOf(field) + 1];

        if (e.key === 'ArrowRight' && segmentAfter(field)) {
            e.preventDefault();
            focusNext(field);
        } else if (e.key === separator && dateTime[field] && nextDateField) {
            e.preventDefault();
            focusSegment(nextDateField);
        } else if ((e.key === 'ArrowLeft' || (e.key === 'Backspace' && !dateTime[field])) && segmentBefore(field)) {
            e.preventDefault();
            focusPrevious(field);
        }
    };

    /**
     * Handles keyboard navigation for hour field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleHourKeyDown = e => {
        if (e.key === 'ArrowRight' || (e.key === ':' && dateTime.hour)) {
            e.preventDefault();
            minuteRef.current?.focus();
        } else if (e.key === 'ArrowLeft' && showDate) {
            e.preventDefault();
            focusPrevious('hour');
        } else if (e.key === 'Backspace' && !dateTime.hour && showDate) {
            e.preventDefault();
            focusPrevious('hour');
        }
    };

    /**
     * Handles keyboard navigation for minute field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleMinuteKeyDown = e => {
        if (e.key === 'ArrowRight' || (e.key === ' ' && dateTime.minute)) {
            e.preventDefault();
            ampmRef.current?.focus();
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            hourRef.current?.focus();
        } else if (e.key === 'Backspace' && !dateTime.minute) {
            e.preventDefault();
            hourRef.current?.focus();
        }
    };

    /**
     * Handles keyboard navigation for AM/PM field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleAmpmKeyDown = e => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            minuteRef.current?.focus();
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            minuteRef.current?.focus();
        }
    };

    /**
     * Handles clicks on the input container to focus the first segment
     */
    const handleContainerClick = () => {
        if (!focusedSegment) {
            focusSegment(segmentSequence[0]);
        }
    };

    /**
     * Toggles the calendar picker visibility
     */
    const handleCalendarIconClick = () => {
        setIsCalendarOpen(prev => !prev);
    };

    /**
     * Handles date selection from the calendar picker
     * @param {Date} date - Selected date from calendar
     */
    const handleCalendarChange = date => {
        const newDateTime = getDateObject(date, showDate, showTime);
        setDateTime(newDateTime);
        notifyChange(newDateTime);
        setIsCalendarOpen(false);
    };

    /**
     * Closes the calendar picker when clicking outside
     */
    const handleClickOutside = () => {
        setIsCalendarOpen(false);
    };

    /**
     * Handles month changes from the calendar dropdown
     * @param {Date} date - Date representing the new month selection
     */
    const handlePickerMonthChange = date => {
        const newDateTime = {
            ...dateTime,
            month: String(date.getMonth() + 1).padStart(2, '0')
        };
        setDateTime(newDateTime);
        notifyChange(newDateTime);
    };

    /**
     * Handles year changes from the calendar dropdown
     * @param {Date} date - Date representing the new year selection
     */
    const handlePickerYearChange = date => {
        const newDateTime = {
            ...dateTime,
            year: String(date.getFullYear())
        };
        setDateTime(newDateTime);
        notifyChange(newDateTime);
    };

    /**
     * Listens for ESC key to close the calendar when open
     */
    useEffect(() => {
        const handleEscKey = event => {
            if (event.key === 'Escape' && isCalendarOpen) {
                setIsCalendarOpen(false);
            }
        };

        if (isCalendarOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isCalendarOpen]);

    // The hidden input drives form submission (`form.jsx` reads it via
    // `inputField.current.value`). For datetime questions we send an ISO 8601 UTC
    // instant so the absolute moment the participant picked survives the round-trip
    // through `Person::formatCustomData`'s `strtotime`/`date()` normalization in
    // UTC server time — letting the researcher render it in their profile TZ
    // (e.g. participant in Warsaw picks 12:22 PM → 10:22Z → 03:22 AM Pacific).
    // Date-only and time-only questions stay TZ-agnostic: a calendar day or clock
    // time isn't an absolute instant, so ISO-encoding them would shift the
    // calendar day around midnight for users east/west of UTC.
    const isDateTime = isDateTimeQuestion(showTimeSelectOnly, showTimeSelect);

    // Only convert to UTC for datetime questions with actual time values.
    // Date-only values (midnight) should not be converted to avoid timezone shift.
    const shouldConvertToUtc = isDateTime && !isDateOnly(value);

    const formattedValue = !value
        ? ''
        : shouldConvertToUtc
        ? moment(value).toISOString()
        : moment(value).format(getStorageFormat(showTimeSelectOnly, showTimeSelect));

    return (
        <>
            <input type="hidden" name={name} ref={inputRef} value={formattedValue} />
            {/* `dir="ltr"` is deliberate, and holds inside an RTL form. A date is a
                numeric sequence, and W3C i18n guidance is that numbers are not
                mirrored: MM/DD/YYYY and HH:mm read left-to-right in Arabic too.
                Pinning the direction here also keeps the segments' visual order in
                step with their tab order and with the ArrowLeft/ArrowRight
                segment navigation below, none of which would survive a flip. */}
            <div
                dir="ltr"
                className={classNames(
                    'relative flex items-center px-3 py-2 h-8 ring-1 ring-shiva rounded hover:ring-bluest-blue w-min',
                    focusedSegment && 'ring-perfect-blue hover:ring-perfect-blue !ring-2'
                )}
                onClick={handleContainerClick}
                aria-labelledby={ariaLabelledby}
            >
                {dateFields.map((field, index) => (
                    <React.Fragment key={field}>
                        {index > 0 && <span className="text-secondary mx-1">{separator}</span>}
                        <DateSegment
                            {...DATE_SEGMENTS[field]}
                            inputRef={segmentRefs[field]}
                            value={dateTime[field]}
                            onValueChange={handleDateFieldChange(field)}
                            onFocus={() => setFocusedSegment(field)}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleDateFieldKeyDown(field)}
                        />
                    </React.Fragment>
                ))}

                {showDate && showTime && <span className="mx-1 text-secondary"> </span>}

                {showTime && (
                    <>
                        <DateSegment
                            inputRef={hourRef}
                            value={dateTime.hour}
                            placeholder="HH"
                            maxLength={2}
                            max={12}
                            min={1}
                            onValueChange={handleHourChange}
                            onFocus={() => setFocusedSegment('hour')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleHourKeyDown}
                            ariaLabel="Hour"
                        />
                        <span className="text-secondary">:</span>
                        <DateSegment
                            inputRef={minuteRef}
                            value={dateTime.minute}
                            placeholder="mm"
                            maxLength={2}
                            max={59}
                            min={0}
                            onValueChange={handleMinuteChange}
                            onFocus={() => setFocusedSegment('minute')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleMinuteKeyDown}
                            ariaLabel="Minute"
                        />
                        <span className="mx-1 text-secondary"> </span>
                        <AmPmSegment
                            inputRef={ampmRef}
                            value={dateTime.ampm}
                            onValueChange={handleAmpmChange}
                            onFocus={() => setFocusedSegment('ampm')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleAmpmKeyDown}
                            ariaLabel="AM/PM"
                        />
                    </>
                )}
                <span className="mx-1" />
                <Button
                    type="button"
                    variant="transparent-icon"
                    onClick={e => {
                        e.stopPropagation();
                        handleCalendarIconClick();
                    }}
                    className="!p-1 !ml-auto !h-6"
                    aria-label={showTimeSelectOnly ? 'Open time picker' : 'Open calendar'}
                >
                    {showTimeSelectOnly ? <FiClock className="!m-0" /> : <FiCalendar className="!m-0" />}
                </Button>
            </div>
            {isCalendarOpen && (
                // Two concerns, deliberately on two elements.
                //
                // The outer div positions the popover and must *inherit* the form's
                // direction: `start-0` (`inset-inline-start`) resolves against the
                // element's own direction, so putting `dir="ltr"` here would pin the
                // calendar to the containing block's left edge while the input sits
                // at its right edge under RTL — the popover lands on the far side of
                // the row.
                //
                // The inner div pins react-datepicker's own chrome LTR: its nav
                // carets are built from borders and the month/time split uses float,
                // all physical, so mirroring would break the layout without buying a
                // localized calendar. Localizing + mirroring the calendar belongs
                // with the datepicker-locale work, alongside the rest of the chrome.
                <div className="absolute z-1000 top-full start-0 mt-1">
                    <div dir="ltr">
                        <AppDatePicker
                            selected={value}
                            onChange={handleCalendarChange}
                            onClickOutside={handleClickOutside}
                            onMonthChange={handlePickerMonthChange}
                            onYearChange={handlePickerYearChange}
                            inline
                            todayButton={!showTimeSelectOnly ? 'Today' : undefined}
                            showYearDropdown={!showTimeSelectOnly}
                            showMonthDropdown={!showTimeSelectOnly}
                            dropdownMode="select"
                            showTimeSelect={showTimeSelect || showTimeSelectOnly}
                            showTimeSelectOnly={showTimeSelectOnly}
                            timeIntervals={15}
                            timeCaption="Time"
                        />
                    </div>
                </div>
            )}
        </>
    );
};
